import type { LinkedTextElement, TextBox, TextChain } from '@/api/types/stowed-element'

type AnyRecord = Record<string, unknown>

function asRecord(value: unknown): AnyRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as AnyRecord
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function getNsDocument(metadata: unknown): AnyRecord | null {
  const m = asRecord(metadata)

  if (!m) return null
  return (asRecord(m.nsDocument) ?? asRecord(m.NsDocument)) as AnyRecord | null
}

function parseTextBox(raw: unknown): TextBox | null {
  const r = asRecord(raw)

  if (!r) return null

  const uidRaw = r.uid ?? r.UID ?? r.Uid
  const uid = typeof uidRaw === 'string' ? uidRaw : typeof uidRaw === 'number' ? String(uidRaw) : ''

  if (!uid) return null

  return { uid }
}

function parseTextChain(raw: unknown): TextChain | null {
  const r = asRecord(raw)

  if (!r) return null

  const idRaw = r.id ?? r.ID ?? r.Id
  const id = typeof idRaw === 'string' ? idRaw : typeof idRaw === 'number' ? String(idRaw) : ''
  const layerRaw = r.layer ?? r.LAYER ?? r.Layer
  const layer = typeof layerRaw === 'string' ? layerRaw.toLowerCase() : 'userlayer'

  const textBoxRaw = asArray(r.textBox) ?? asArray(r.TextBox) ?? asArray(r.textBoxes)
  const textBox: TextBox[] = []

  if (textBoxRaw) {
    for (const tb of textBoxRaw) {
      const parsed = parseTextBox(tb)

      if (parsed) textBox.push(parsed)
    }
  }

  if (!id || textBox.length < 2) return null

  return { id, layer, textBox }
}

/**
 * Parses `nsDocument.linkedTextElements.textChain` from checkpoint metadata into
 * the wire-shaped `LinkedTextElement`. Chains with fewer than 2 members are dropped
 * (a chain of 1 carries no flow information).
 */
export function checkpointToLinkedTextElements(metadata: unknown): LinkedTextElement | null {
  const nsDocument = getNsDocument(metadata)

  if (!nsDocument) return null

  const linkedTextElements =
    asRecord(nsDocument.linkedTextElements) ?? asRecord(nsDocument.LinkedTextElements)

  if (!linkedTextElements) return null

  const textChainRaw =
    asArray(linkedTextElements.textChain) ?? asArray(linkedTextElements.TextChain)

  if (!textChainRaw) return null

  const textChain: TextChain[] = []

  for (const c of textChainRaw) {
    const parsed = parseTextChain(c)

    if (parsed) textChain.push(parsed)
  }

  if (textChain.length === 0) return null

  return { textChain }
}
